import { useCallback, useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { useGetOwnerDocuments } from '../../hooks/backend/owners'
import { useGetDocumentsSyncedAt } from '../../hooks/backend/shared'
import { useCapabilities } from '../../hooks/useCapabilities'
import { DocumentsBody } from '../documents/DocumentsBody'
import { DocumentPackDownload } from '../documents/DocumentPackDownload'
import type { UploadCategory } from '../documents/UploadControl'
import type { DocumentRow } from '../../utils/documents'
import type { OwnerRow } from '../../utils/owners'

const OWNER_CATEGORIES: UploadCategory[] = [
  { code: 'owner_information', label: 'Owner information' },
  // Nothing records whether an owner is a company (9970), so this is offered
  // for every owner and staff pick it for the company ones. Short, because the
  // picker is narrow; the list heading spells it out.
  { code: 'company_registration', label: 'Company BRNC' },
  { code: 'onboarding', label: 'Onboarding documents' },
]

export function OwnerDocumentsDialog({ owner }: { owner: OwnerRow }) {
  const [open, setOpen] = useState(false)
  const docs = useGetOwnerDocuments()
  const synced = useGetDocumentsSyncedAt()
  const { canView, canEditOwners, isAdmin } = useCapabilities()
  const personId = owner.person_id

  const refetch = useCallback(() => {
    if (!personId) return
    docs.trigger({ person_id: personId }, { skipCache: true })
    synced.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId])

  // Fetch fresh on each open.
  useEffect(() => {
    if (!open || !personId) return
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, personId])

  const rows = (docs.data as DocumentRow[] | undefined) ?? []
  const syncState = synced.data as
    | { last_synced: string | null; is_stale: boolean | null }
    | undefined
  const syncedAt = syncState?.last_synced ?? null
  const syncStale = syncState?.is_stale ?? null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs">
          <FileText className="h-3.5 w-3.5" />
          Documents
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{owner.full_name || 'Owner'}</DialogTitle>
          <DialogDescription>Owner documents</DialogDescription>
        </DialogHeader>
        <DocumentsBody
          rows={rows}
          loading={docs.loading}
          error={docs.error}
          emptyText="We hold no documents for this owner yet: nothing in Drive, onboarding or the driver record."
          syncedAt={syncedAt}
          syncStale={syncStale}
          categories={OWNER_CATEGORIES}
          uploadScope={personId ? { owner_person_id: personId } : {}}
          canUpload={canView && !!personId}
          canDelete={isAdmin || canEditOwners}
          attachesTo="owner"
          onChanged={refetch}
        />
        {/* The statement evidence pack, which is a different thing from the KYC
            documents above: those are what we hold ABOUT the owner, this is what
            stands behind what we charged him. Same component on the vehicle
            dialog. */}
        {personId && (
          <DocumentPackDownload
            scope={{ person_id: personId }}
            scopeLabel={owner.full_name || 'Owner'}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
